import { useState } from 'react';
import { api, setSessionToken } from '../api.js';
import { useSession } from '../session.js';
import { Drawer, useToast } from '../ui.js';

/**
 * Change your own password, knowing the current one.
 *
 * The everyday half of account recovery, and the half that should be reachable
 * without asking anybody: a temporary password handed over by management is
 * meant to be replaced here within the minute.
 */
export function ChangePassword({ onClose }: { onClose: () => void }): JSX.Element {
  const { refresh } = useSession();
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const short = next.length > 0 && next.length < 10;
  const mismatch = confirm.length > 0 && confirm !== next;
  const ready = !!current && next.length >= 10 && confirm === next;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const { token } = await api.post<{ token?: string }>('/password/change', {
        currentPassword: current, newPassword: next,
      });
      // The old password's sessions are gone, this tab's included; the response
      // carries the replacement so the screen behind this one stays open.
      setSessionToken(token);
      await refresh();
      toast.ok('Password changed', 'Anywhere else you were signed in has been signed out.');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change that');
      setBusy(false);
    }
  };

  return (
    <Drawer
      title="Change password"
      subtitle="Everywhere else you are signed in will be signed out"
      onClose={onClose}
      narrow
      footer={(
        <>
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn--primary" onClick={submit} disabled={busy || !ready}>
            {busy ? 'Changing…' : 'Change password'}
          </button>
        </>
      )}
    >
      <form className="stack" onSubmit={(e) => { e.preventDefault(); if (ready) submit(); }}>
        <label className="field">
          <span>Current password</span>
          <input className="input" type="password" autoComplete="current-password" value={current}
            onChange={(e) => setCurrent(e.target.value)} autoFocus />
        </label>
        <label className="field">
          <span>New password</span>
          <input className="input" type="password" autoComplete="new-password" value={next}
            onChange={(e) => setNext(e.target.value)} />
        </label>
        <label className="field">
          <span>Type it again</span>
          <input className="input" type="password" autoComplete="new-password" value={confirm}
            onChange={(e) => setConfirm(e.target.value)} />
        </label>
        <p className="small muted" style={{ margin: 0 }}>
          {short ? `At least 10 characters — ${10 - next.length} more.`
            : mismatch ? 'Those two do not match.'
            : 'At least 10 characters.'}
        </p>
        {error && (
          <div style={{
            padding: 8, background: 'var(--conflict-bg)', border: '1px solid var(--conflict-bd)',
            color: 'var(--conflict-c)', borderRadius: 6, fontSize: 12.5,
          }}>
            {error}
          </div>
        )}
        {/* Submitting on Enter without a visible submit button needs one here. */}
        <button type="submit" style={{ display: 'none' }} aria-hidden />
      </form>
    </Drawer>
  );
}
